import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import {
  microphoneOutlineIcon,
  microphoneSolidIcon,
} from '@progress/kendo-svg-icons';
import {
  DICTATION_ENGINE,
  DictationError,
  DictationSession,
} from './dictation-engine';

export const DICTATION_MESSAGES: Record<DictationError, string> = {
  denied:
    'Microphone access is blocked. Allow it in the browser to speak, or type your ask.',
  'no-microphone': 'No microphone was found. You can still type your ask.',
  'no-speech': 'No speech was heard. Tap Speak and try again.',
  failed: 'Speech input stopped. You can still type your ask.',
};

// US 35405: a mic button for typed Ask Ryn prompts. Tap Speak, talk, tap Stop;
// the heard text streams out as `dictated` so the host can fill its prompt and
// the user can edit before sending. Hidden where the browser has no engine.
// Becoming `disabled` (the host is sending) stops listening.
@Component({
  selector: 'ups-ask-ryn-dictation-button',
  standalone: true,
  imports: [ButtonsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dictation-button.component.html',
  styleUrl: './dictation-button.component.scss',
})
export class AskRynDictationButtonComponent {
  private readonly engine = inject(DICTATION_ENGINE);
  readonly disabled = input(false);
  // Fires when listening starts, so the host can remember its current text.
  readonly started = output<void>();
  // The whole text heard so far this session.
  readonly dictated = output<string>();

  protected readonly supported = this.engine.supported;
  protected readonly speakIcon = microphoneOutlineIcon;
  protected readonly stopIcon = microphoneSolidIcon;
  private readonly session = signal<DictationSession | null>(null);
  private readonly error = signal<DictationError | null>(null);
  protected readonly listening = computed(() => !!this.session());
  protected readonly message = computed(() => {
    const code = this.error();
    return code ? DICTATION_MESSAGES[code] : '';
  });
  private destroyed = false;

  constructor() {
    effect(() => {
      if (this.disabled()) untracked(() => this.session()?.stop());
    });
    inject(DestroyRef).onDestroy(() => {
      this.destroyed = true;
      this.session()?.stop();
    });
  }

  protected toggle(): void {
    const current = this.session();
    if (current) {
      current.stop();
      return;
    }
    this.error.set(null);
    this.started.emit();
    let active: DictationSession | null = null;
    let ended = false;
    active = this.engine.start({
      text: (transcript) => {
        if (!this.destroyed && this.session() === active)
          this.dictated.emit(transcript);
      },
      error: (code) => {
        if (!this.destroyed) this.error.set(code);
      },
      ended: () => {
        ended = true;
        if (this.session() === active) this.session.set(null);
      },
    });
    // An engine may end synchronously (unsupported, or start() threw).
    if (!ended) this.session.set(active);
  }
}
